/**
 * Free Shipping Progress Hook
 *
 * Fetches the free shipping threshold from shipping-go (backoffice-configurable)
 * and computes progress state relative to the current cart total.
 *
 * The threshold comes from `PricingRule.free_shipping_from` on active shipping
 * methods, filtered by the user's currency. When no threshold is configured
 * for the currency the feature is disabled.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { getFreeShippingThresholdViaBff } from '@/infrastructure/services/bff/shipping.bff-service';

export interface FreeShippingState {
  /** Threshold in cents. 0 when feature is disabled. */
  threshold: number;
  /** Remaining amount in cents to reach free shipping. 0 when unlocked. */
  remaining: number;
  /** Progress percentage 0–100. */
  percentage: number;
  /** True when cart total >= threshold. */
  isUnlocked: boolean;
  /** False when threshold is not configured for this currency. */
  isEnabled: boolean;
  /** True while the threshold is being fetched from shipping-go. */
  isLoading: boolean;
}

const DISABLED_STATE: FreeShippingState = {
  threshold: 0,
  remaining: 0,
  percentage: 0,
  isUnlocked: false,
  isEnabled: false,
  isLoading: false,
};

/**
 * @param cartTotal    - current cart total in cents (display amount)
 * @param currencyCode - ISO 4217 currency code (e.g. "USD")
 */
export function useFreeShipping(
  cartTotal: number,
  currencyCode: string,
): FreeShippingState {
  const { data: threshold, isLoading } = useQuery({
    queryKey: ['free-shipping-threshold', currencyCode],
    queryFn: () => getFreeShippingThresholdViaBff(currencyCode),
    staleTime: 10 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
    enabled: currencyCode.length > 0,
  });

  return useMemo(() => {
    if (isLoading) {
      return { ...DISABLED_STATE, isLoading: true };
    }

    if (!threshold || threshold <= 0) {
      return DISABLED_STATE;
    }

    const percentage = Math.min((cartTotal / threshold) * 100, 100);
    const remaining = Math.max(threshold - cartTotal, 0);
    const isUnlocked = cartTotal >= threshold;

    return {
      threshold,
      remaining,
      percentage,
      isUnlocked,
      isEnabled: true,
      isLoading: false,
    };
  }, [cartTotal, threshold, isLoading]);
}
